import React, { useEffect, useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { getArtist, updateArtist } from '../../api/artists'
import { snakeToCamel } from './AdminEditRelease'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { CircularProgress, Alert, Snackbar } from '@mui/material'
import { AuthErrorCodes } from 'firebase/auth'

const AdminEditArtist = () => {
    //////////////////////////////////////////////
    ////////////////// BACKEND ///////////////////
    //////////////////////////////////////////////

    const { index, uid } = useParams()
    const queryClient = useQueryClient()
    const [openSnackbar, setOpenSnackbar] = useState(false)
    const [errorMessage, setErrorMessage] = useState('')

    const [name, setName] = useState('')
    const [website, setWebsite] = useState('')
    const [facebook, setFacebook] = useState('')
    const [twitter, setTwitter] = useState('')
    const [instagram, setInstagram] = useState('')
    const [youtube, setYoutube] = useState('')
    const [spotify, setSpotify] = useState('')
    const [apple, setApple] = useState('')
    const [deezer, setDeezer] = useState('')
    const [soundCloud, setSoundCloud] = useState('')

    const successBtn = useRef()

    //////////// GET USER'S ARTIST DATA ////////////
    const { data: artist, isLoading, error } = useQuery({
        queryKey: ['artist', uid, index],
        queryFn: () => getArtist(uid, index),
        select: (data) => {
            
            return data.artist
        },
        onError: (err) => {
            setErrorMessage('Failed to load artist data: ' + err.message)
            setOpenSnackbar(true)
        }
    })

    const initialValues = (item) => {
        setName(item.name)
        setWebsite(item.website)
        setFacebook(item.facebook)
        setTwitter(item.twitter)
        setInstagram(item.instagram)
        setYoutube(item.youtube)
        setSpotify(item.spotify)
        setApple(item.apple)
        setDeezer(item.deezer)
        setSoundCloud(item.soundCloud)
    }
    useEffect(()=> {
        if(artist){
            initialValues(snakeToCamel(artist))
        }
    }, [isLoading, artist])

    //////////// UPDATE ARTIST DATA ////////////
    const updateArtistMutation = useMutation({
        mutationFn: (artistData) => updateArtist(uid, index, artistData),
        onSuccess: () => {
            // Invalidate and refetch artist data
            queryClient.invalidateQueries({ queryKey: ['artist', uid, index] })
            queryClient.invalidateQueries({ queryKey: ['artists'] })
            // Show success message
            successBtn.current?.click()
        },
        onError: (error) => {
            setErrorMessage('Failed to update artist: ' + error.message)
            setOpenSnackbar(true)
            console.error("Failed to update artist:", error)
        }
    })
    
    const editArtist = async () => {
        const artistData = {
            name,
            website,
            facebook,
            twitter,
            instagram,
            youtube,
            spotify,
            apple,
            deezer,
            soundCloud
        }
        
        updateArtistMutation.mutate(artistData)
    }

    const handleCloseSnackbar = () => {
        setOpenSnackbar(false)
    }

    return (
        <div className='dash-section'>
            <h1 className='dash-title'>Edit Artist</h1>
            <div className="row">
                <div className="col-12 mt-4">
                    <div className="dash-card">
                        {isLoading ? (
                            <div className="d-flex justify-content-center align-items-center py-5">
                                <CircularProgress size={40} />
                                <p className="ms-3 mb-0">Loading artist data...</p>
                            </div>
                        ) : (error)  ? (
                            <Alert severity="error" className="m-3">
                                Failed to load artist data. Please try again. {error.message}
                            </Alert>
                        ) : (
                            <div className="row">
                                <div className="col-12">
                                    <h4 className='dash-card-title'>
                                        <Link to={'/admin/artists'} className='title-back-link'>
                                            <i className="fi fi-rr-arrow-small-left"></i>
                                        </Link>
                                        Edit Artist
                                    </h4>
                                </div>
                                <div className="col-md-6 mt-3">
                                    <div className="dash-form-group">
                                        <label>Name</label>
                                        <input value={name} onChange={(e) => setName(e.target.value)} type="text" placeholder='Name' className='dash-form-input' />
                                    </div>
                                </div>
                                <div className="col-md-6 mt-3">
                                    <div className="dash-form-group">
                                        <label>Website</label>
                                        <input value={website} onChange={(e) => setWebsite(e.target.value)} type="text" placeholder='Website' className='dash-form-input' />
                                    </div>
                                </div>
                                <div className="col-12 mt-4">
                                    <h4 className='dash-form-heading'>Profile Information</h4>
                                </div>
                                <div className="col-md-6 mt-3">
                                    <div className="dash-form-group">
                                        <label>Spotify</label>
                                        <input value={spotify} onChange={(e) => setSpotify(e.target.value)} type="text" placeholder='Spotify' className='dash-form-input' />
                                    </div>
                                </div>
                                <div className="col-md-6 mt-3">
                                    <div className="dash-form-group">
                                        <label>Apple Music</label>
                                        <input value={apple} onChange={(e) => setApple(e.target.value)} type="text" placeholder='Apple' className='dash-form-input' />
                                    </div>
                                </div>
                                <div className="col-md-6 mt-3">
                                    <div className="dash-form-group">
                                        <label>Facebook</label>
                                        <input value={facebook} onChange={(e) => setFacebook(e.target.value)} type="text" placeholder='Facebook' className='dash-form-input' />
                                    </div>
                                </div>
                                <div className="col-md-6 mt-3">
                                    <div className="dash-form-group">
                                        <label>Instagram</label>
                                        <input value={instagram} onChange={(e) => setInstagram(e.target.value)} type="text" placeholder='Instagram' className='dash-form-input' />
                                    </div>
                                </div>
                                <div className="col-md-6 mt-3">
                                    <div className="dash-form-group">
                                        <label>Youtube</label>
                                        <input value={youtube} onChange={(e) => setYoutube(e.target.value)} type="text" placeholder='Youtube' className='dash-form-input' />
                                    </div>
                                </div>
                                <div className="col-md-6 mt-3">
                                    <div className="dash-form-group">
                                        <label>X</label>
                                        <input value={twitter} onChange={(e) => setTwitter(e.target.value)} type="text" placeholder='Twitter' className='dash-form-input' />
                                    </div>
                                </div>
                                <div className="col-md-6 mt-3">
                                    <div className="dash-form-group">
                                        <label>Deezer</label>
                                        <input value={deezer} onChange={(e) => setDeezer(e.target.value)} type="text" placeholder='Deezer' className='dash-form-input' />
                                    </div>
                                </div>
                                <div className="col-md-6 mt-3">
                                    <div className="dash-form-group">
                                        <label>SoundCloud</label>
                                        <input value={soundCloud} onChange={(e) => setSoundCloud(e.target.value)} type="text" placeholder='SoundCloud' className='dash-form-input' />
                                    </div>
                                </div>
                                <div className="col-12 mt-3">
                                    <div className="dash-form-btns">
                                        <button 
                                            className='dash-form-btn' 
                                            onClick={editArtist}
                                            disabled={updateArtistMutation.isPending}
                                        >
                                            {updateArtistMutation.isPending ? (
                                                <>
                                                    <CircularProgress size={20} color="inherit" className="me-2" />
                                                    Updating...
                                                </>
                                            ) : "Submit"}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            
            {/* Error Snackbar */}
            <Snackbar
                open={openSnackbar}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
            >
                <Alert onClose={handleCloseSnackbar} severity="error" sx={{ width: '100%' }}>
                    {errorMessage}
                </Alert>
            </Snackbar>
            
            {/* Success Modal */}
            <button ref={successBtn} data-bs-target="#successModal" data-bs-toggle="modal" hidden></button>
            <div className="modal fade" id="successModal" tabIndex="-1" data-bs-backdrop="static" aria-labelledby="successModalLabel" aria-hidden="true">
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content">
                        <div className="modal-body success-modal">
                            <a className='success-close-btn' href="/admin/artists">
                                <i className="fi fi-rr-cross"></i>
                            </a>
                            <i className="fi fi-rr-check-circle text-success success-icon"></i>
                            <h5>Artist Updated Successfully!</h5>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default AdminEditArtist